import React, { useContext } from 'react';
import cn from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import ArtStage from '../../../ArtStage';
import Details from '../../../DeviationViews/Details';
import TextualContent from '../../../DeviationViews/TextualContent/TextualContent';
import MonetizationBlock from '../../../Sidebar/SidebarBlock/layouts/MonetizationBlock';
import Sidebar from '../../../Sidebar';
import StashBanner from '../../../banners/StashBanner';
import ActionBar from '../../../ActionBar';
import { useIsCurrentUserDeviationAuthor } from '../../../utils/hooks/useIsCurrentUserDeviationAuthor';
import { isExtendedDeviationFullyLoaded } from '../../redux/helpers';
import CentralColumn from '../CentralColumn';
import Comments from '../Comments';
import AdoptableInfo from '../../../Sidebar/SidebarBlock/layouts/AdoptableInfo';
import PcpInfo from '../../../Sidebar/SidebarBlock/layouts/PcpInfo';

import s from './StashPageContent.scss';

export interface Props {
  deviation: PapiDeviation;
}

const StashPageContent: React.FC<Props> = ({ deviation }) => {
  const isMobile = useContext(MobileContext);
  const isAuthor = useIsCurrentUserDeviationAuthor(deviation);

  const isExtendedLoaded = isExtendedDeviationFullyLoaded(deviation);
  const monetizationBlock = deviation.extended?.relatedContent?.find(
    block =>
      block.contentType.includes('monetization_') ||
      block.contentType === 'monetization_promotion_adoptable'
  );
  const showMobileAdoptInfoCard = isMobile && !!deviation.extended?.adoptable;
  const showMobilePcpInfoCard = isMobile && deviation.pcp && deviation.extended?.pcp;

  const isRaisingMonetizationBlock = isMobile && isExtendedLoaded && monetizationBlock;

  return (
    <div className={s['root']}>
      <div className={s['stage']}>
        <ArtStage deviation={deviation} />
        {!isMobile && isAuthor && <StashBanner className={s['banner']} />}
        <ActionBar deviation={deviation} />
        {showMobileAdoptInfoCard && <AdoptableInfo deviation={deviation} />}
        {showMobilePcpInfoCard && <PcpInfo className={s['upper-pcp-info']} deviation={deviation} />}
        <CentralColumn className={s['text-and-details']}>
          <TextualContent deviation={deviation} />
          <Details deviation={deviation} />
        </CentralColumn>

        {isRaisingMonetizationBlock && (
          <BiLoggerContextProvider
            value={{
              sectionname: monetizationBlock.contentType,
            }}
          >
            <MonetizationBlock block={monetizationBlock} deviation={deviation} />
          </BiLoggerContextProvider>
        )}
      </div>

      <div className={s['meta']}>
        <CentralColumn supportSidebar>
          <Comments deviation={deviation} className={s['comments']} />
        </CentralColumn>
      </div>

      <Sidebar
        deviation={deviation}
        className={cn(s['sidebar'], !deviation.isPublished && s['unpublished'])}
      />
    </div>
  );
};
StashPageContent.displayName = 'StashPageContent';

export default StashPageContent;
